import { useCallback, useEffect, useState } from 'react';
import { DashboardHeader } from '@/components/dashboard/DashboardHeader';
import { VitalsPanel } from '@/components/dashboard/VitalsPanel';
import { CoffersPanel } from '@/components/dashboard/CoffersPanel';
import { SkillsPanel } from '@/components/dashboard/SkillsPanel';
import { BattleStatsPanel } from '@/components/dashboard/BattleStatsPanel';
import { QuickActionsPanel } from '@/components/dashboard/QuickActionsPanel';
import { TutorialBanner } from '@/components/dashboard/TutorialBanner';
import { QuestRunner } from '@/components/dialogue/QuestRunner';
import { RewardToast } from '@/components/ui/RewardToast';
import { useCharacter } from '@/hooks/useCharacter';
import { useAuthStore } from '@/stores/auth';
import { supabase } from '@/lib/supabase';
import { getQuestDefinition, getTutorialQuestId } from '@/data/quests';
import { getTutorialQuest } from '@/data/tutorialQuests';
import { startQuest, getQuestProgress } from '@/lib/questEngine';
import { TEXTURES } from '@/lib/assets';
import { npcPortrait } from '@/lib/assets';
import { NPCS } from '@/data/npcs';
import type { QuestDefinition, QuestRewards } from '@/types/quests';

interface ActiveQuest {
  quest: QuestDefinition;
  stageKey: string;
}

interface ToastData {
  rewards: QuestRewards;
  message: string;
}

export default function Dashboard(): JSX.Element {
  const character = useCharacter();
  const setCharacter = useAuthStore((s) => s.setCharacter);
  const [activeQuest, setActiveQuest] = useState<ActiveQuest | null>(null);
  const [tutorialModal, setTutorialModal] = useState(false);
  const [toast, setToast] = useState<ToastData | null>(null);
  const refreshCharacter = useAuthStore((s) => s.refreshCharacter);

  // Poll every 60s: apply regen server-side and update local state directly.
  useEffect(() => {
    if (!character) return;
    const characterId = character.id;
    const interval = setInterval(async () => {
      try {
        const { data } = await supabase.functions.invoke('refresh-character', {
          body: { characterId },
        });
        if (data?.ok && data.character) {
          setCharacter(data.character);
        }
      } catch {
        // Silent — next tick will retry.
      }
    }, 60_000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [character?.id, setCharacter]);

  const handleTutorialClick = useCallback(async () => {
    if (!character) return;

    // Quest 1 uses the existing quest-action system
    if (character.tutorial_quest_current === 1) {
      const questId = getTutorialQuestId(character.tutorial_quest_current);
      const quest = getQuestDefinition(questId);
      if (!quest) return;
      let progress = await getQuestProgress(character.id, questId);
      if (!progress || progress.state === 'not_started') {
        progress = await startQuest(character.id, questId);
      }
      if (!progress) return;
      const stageKeys = Object.keys(quest.stages);
      const stageKey =
        progress.state === 'in_progress' && progress.current_stage > 0
          ? (stageKeys[progress.current_stage] ?? quest.initialStage)
          : quest.initialStage;
      setActiveQuest({ quest, stageKey });
      return;
    }

    // Quests 2-9 use the tutorial modal
    setTutorialModal(true);
  }, [character]);

  const handleQuestComplete = useCallback((rewards: QuestRewards) => {
    setActiveQuest(null);
    setToast({
      rewards,
      message: 'Old Pete will not soon forget your name.',
    });
  }, []);

  if (!character) {
    return (
      <main className="min-h-dvh flex items-center justify-center">
        <p className="text-parchment-dark text-sm">Loading your character…</p>
      </main>
    );
  }

  const showTutorialBanner = !character.tutorial_complete && character.tutorial_quest_current <= 9;

  return (
    <div
      className="min-h-dvh"
      style={{
        backgroundImage: `url("${TEXTURES.wood}")`,
        backgroundSize: '600px',
        backgroundRepeat: 'repeat',
      }}
    >
      <div
        className="min-h-dvh"
        style={{
          background: [
            'radial-gradient(ellipse at center, rgba(12,34,51,0.70) 0%, rgba(5,15,25,0.88) 70%)',
            'linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.15) 100%)',
          ].join(', '),
        }}
      >
        <DashboardHeader character={character} />

        <main className="mx-auto max-w-6xl px-3 sm:px-5 py-4 sm:py-6 space-y-4 sm:space-y-5 stage-fade-in">
          {showTutorialBanner && (
            <TutorialBanner
              character={character}
              onClick={() => {
                void handleTutorialClick();
              }}
            />
          )}

          <VitalsPanel character={character} />

          <div className="grid gap-4 sm:gap-5 lg:grid-cols-2">
            <CoffersPanel character={character} />
            <BattleStatsPanel character={character} />
            <div className="lg:col-span-2">
              <SkillsPanel character={character} />
            </div>
          </div>

          <QuickActionsPanel />

          <p className="text-center text-[11px] uppercase tracking-[0.3em] text-parchment-dark/60 pt-2 pb-4">
            The Caribbean awaits
          </p>
        </main>
      </div>

      {activeQuest && (
        <QuestRunner
          quest={activeQuest.quest}
          characterId={character.id}
          initialStageKey={activeQuest.stageKey}
          onComplete={handleQuestComplete}
          onClose={() => setActiveQuest(null)}
        />
      )}

      {tutorialModal && character && (
        <TutorialQuestModal
          character={character}
          onClose={() => setTutorialModal(false)}
          onComplete={async (rewards) => {
            setTutorialModal(false);
            await refreshCharacter();
            if (rewards) {
              setToast({ rewards, message: 'Quest complete.' });
            }
          }}
        />
      )}

      {toast && (
        <RewardToast
          doubloons={toast.rewards.doubloons}
          xp={toast.rewards.xp}
          marks={toast.rewards.marks}
          message={toast.message}
          onDone={() => setToast(null)}
        />
      )}
    </div>
  );
}

function TutorialQuestModal({
  character,
  onClose,
  onComplete,
}: {
  character: {
    id: string;
    tutorial_quest_current: number;
    tutorial_quest_state: Record<string, Record<string, boolean>>;
  };
  onClose: () => void;
  onComplete: (rewards?: QuestRewards) => void;
}): JSX.Element {
  const [loading, setLoading] = useState(false);
  const [phase, setPhase] = useState<'intro' | 'complete'>('intro');
  const quest = getTutorialQuest(character.tutorial_quest_current);

  if (!quest) {
    onClose();
    return <></>;
  }

  const npc = NPCS[quest.npc];
  const portrait = npcPortrait(quest.npc);
  const questState = character.tutorial_quest_state[`quest_${quest.id}`] ?? {};
  const allDone = quest.objectives.every((o) => questState[o.id]);

  async function handleAction(): Promise<void> {
    if (!quest) return;
    setLoading(true);

    if (phase === 'intro') {
      // For quests with auto-complete objectives or action-type objectives
      if (quest.id === 2) {
        // Quest 2: do the tutorial crime
        const { data } = await supabase.functions.invoke('crime-attempt', {
          body: { characterId: character.id, crimeId: 'tutorial_recover_velas_coin' },
        });
        if (data?.ok || data?.outcome) {
          await supabase.rpc('update_tutorial_objective', {
            p_character_id: character.id,
            p_quest_key: 'quest_2',
            p_objective_key: 'crime_completed',
          });
          setPhase('complete');
        }
      } else if (quest.id === 8) {
        // Quest 8: scripted pickpocket (forced escape)
        const { data } = await supabase.functions.invoke('crime-attempt', {
          body: { characterId: character.id, crimeId: 'tutorial_pickpocket' },
        });
        if (data?.ok || data?.outcome) {
          await supabase.rpc('update_tutorial_objective', {
            p_character_id: character.id,
            p_quest_key: 'quest_8',
            p_objective_key: 'pickpocket_attempted',
          });
          setPhase('complete');
        }
      } else if (quest.id === 9) {
        // Quest 9: auto-complete graduation
        setPhase('complete');
      } else if (allDone) {
        setPhase('complete');
      } else {
        // Quest 3/4: objectives require actions outside this modal (training)
        onClose();
      }
    } else {
      // Complete the quest
      const r = quest.reward;
      await supabase.functions.invoke('quest-action', {
        body: {
          action: 'complete_tutorial',
          characterId: character.id,
          questId: quest.id,
          doubloons: r.doubloons ?? 0,
          xp: r.xp ?? 0,
          marks: r.marks ?? 0,
          energyMaxBonus: r.energyMaxBonus ?? 0,
          title: r.title ?? null,
          setTutorialComplete: quest.setTutorialComplete ?? false,
          setPort: quest.setPort ?? null,
          nextQuest: quest.nextQuest ?? null,
          pendingLoot:
            r.pendingLoot?.map((l) => ({ rarity: l.rarity, source_id: l.sourceId })) ?? [],
        },
      });
      onComplete({ doubloons: r.doubloons, xp: r.xp, marks: r.marks });
    }
    setLoading(false);
  }

  const text = phase === 'intro' ? quest.introText : quest.completionText;
  const paragraphs = text.split('\n\n').filter(Boolean);

  const buttonText =
    phase === 'complete'
      ? 'Collect Rewards'
      : quest.id === 2
        ? "Recover Vela's Coin"
        : quest.id === 8
          ? 'Attempt the Pickpocket'
          : quest.id === 9
            ? 'Set Sail for Port Royale'
            : allDone
              ? 'Complete Quest'
              : 'Got it';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-3 py-6 sm:px-6"
      role="dialog"
      aria-modal="true"
    >
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={onClose} />
      <div
        className="relative w-full max-w-2xl max-h-[90dvh] overflow-y-auto rounded-lg border-2 border-wood-mid/70 shadow-[0_8px_40px_rgba(0,0,0,0.7)] stage-fade-in"
        style={{ backgroundImage: `url("${TEXTURES.parchment}")`, backgroundSize: 'cover' }}
      >
        <div className="absolute inset-0 bg-parchment/85 pointer-events-none rounded-lg" />
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 z-10 inline-flex h-8 w-8 items-center justify-center rounded-full border border-wood-mid/40 bg-parchment-light/80 text-ink-mid hover:text-ink text-sm"
          aria-label="Close"
        >
          &times;
        </button>
        <div className="relative px-5 sm:px-8 pt-6 pb-6">
          <div className="flex items-center gap-4 mb-5">
            <div className="shrink-0 h-16 w-16 sm:h-24 sm:w-24 rounded-full overflow-hidden border-2 border-candle/50 shadow-[0_0_12px_rgba(232,177,75,0.2)]">
              <img
                src={portrait}
                alt={npc?.name ?? quest.npc}
                className="h-full w-full object-cover"
                width={96}
                height={96}
              />
            </div>
            <div>
              <h2 className="font-display text-xl sm:text-2xl text-wood-dark">
                {npc?.name ?? quest.npc}
              </h2>
              <p className="text-xs text-ink-mid">{quest.name}</p>
            </div>
          </div>
          <div className="space-y-3 text-ink text-base sm:text-lg leading-relaxed mb-6">
            {paragraphs.map((p, i) => (
              <p key={i}>&ldquo;{p}&rdquo;</p>
            ))}
          </div>
          {phase === 'intro' && quest.objectives.length > 0 && (
            <div className="mb-4 rounded-md border border-wood-mid/30 bg-parchment-light/50 px-3 py-2">
              <p className="text-xs font-display uppercase tracking-wider text-wood-mid mb-1">
                Objectives
              </p>
              {quest.objectives.map((o) => (
                <div key={o.id} className="flex items-center gap-2 text-sm text-ink">
                  <span className={questState[o.id] ? 'text-emerald-600' : 'text-ink-light'}>
                    {questState[o.id] ? '✓' : '○'}
                  </span>
                  <span className={questState[o.id] ? 'line-through text-ink-light' : ''}>
                    {o.description}
                  </span>
                </div>
              ))}
            </div>
          )}
          <button
            type="button"
            disabled={loading}
            onClick={() => {
              void handleAction();
            }}
            className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-sea-deep text-candle border-2 border-candle/50 px-5 py-3 font-display tracking-[0.2em] uppercase text-sm shadow-sm hover:bg-sea-dark hover:border-candle transition-all min-h-12 disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-candle"
          >
            {loading && (
              <span
                aria-hidden
                className="inline-block h-3.5 w-3.5 rounded-full border-2 border-candle/40 border-t-candle animate-spin"
              />
            )}
            <span>{buttonText}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
